/**
 * kea-coupon-gift.js
 */

((document) => {
    'use strict';

    const NS = '[KeaCouponGift]';
    let isUpdating = false;
    let rescueAttempts = {};
    let checkTimer = null;

    // Sauvegarder le fetch natif AVANT tout monkey-patching
    const nativeFetch = window.fetch.bind(window);

    /**
     * Rafraîchit le drawer via le mécanisme natif du thème
     */
    function refreshDrawer() {
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
            drawer.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true,
                detail: { replacementDelay: 400 }
            }));
        }
    }

    /**
     * Applique un code promo en conservant ceux déjà présents (Format Robuste)
     * Utilise nativeFetch pour ne pas déclencher les intercepteurs
     */
    async function applyDiscountCodeWithPersistence(code) {
        try {
            await nativeFetch('/discount/' + encodeURIComponent(code));

            await nativeFetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates: {} })
            });

            return await nativeFetch('/cart.js').then(res => res.json());
        } catch (e) {
            return null;
        }
    }

    /**
     * Extrait les IDs de variantes des coupons KEA
     */
    function extractCouponVariantIds(cart) {
        const ids = [];
        const allCodes = new Set();

        const apps = [...(cart.discount_applications || []), ...(cart.cart_level_discount_applications || [])];
        apps.forEach(d => { const c = d.code || d.title || ''; if (c) allCodes.add(c.toUpperCase()); });

        (cart.items || []).forEach(item => {
            (item.line_level_discount_allocations || []).forEach(alloc => {
                const c = alloc.discount_application ? (alloc.discount_application.code || alloc.discount_application.title) : '';
                if (c) allCodes.add(c.toUpperCase());
            });
        });

        allCodes.forEach(code => {
            const match = code.match(/^KEA(?:-ID-)?(\d+)$/i);
            if (match && match[1]) ids.push(match[1]);
        });
        return ids;
    }

    /**
     * Analyse et synchronisation du panier
     * Verrouillé par isUpdating pour empêcher les exécutions parallèles
     */
    async function checkCouponGifts(cart) {
        if (isUpdating || !cart) return;
        isUpdating = true;

        try {
            const couponVariantIds = extractCouponVariantIds(cart);
            const existingCouponGifts = cart.items.filter(item => item.properties && item.properties._source === 'coupon_gift');

            let needsRefresh = false;

            // 1. AJOUTER les cadeaux manquants ou FORCER la quantité à 1
            for (const variantId of couponVariantIds) {
                // On cible UNIQUEMENT la ligne qui a la propriété du cadeau de la roue
                const giftItems = cart.items.filter(item =>
                    String(item.variant_id) === String(variantId) &&
                    item.properties &&
                    item.properties._source === 'coupon_gift'
                );

                if (giftItems.length === 0) {
                    await addGiftAction(variantId);
                    needsRefresh = true;
                } else {
                    // Supprimer les lignes en double s'il y en a
                    for (let i = 1; i < giftItems.length; i++) {
                        await removeGiftAction(giftItems[i].key);
                        needsRefresh = true;
                    }
                    // Forcer la quantité à 1 sur la première ligne
                    if (giftItems[0].quantity !== 1) {
                        await setGiftQuantity(giftItems[0].key, 1);
                        needsRefresh = true;
                    }
                }
            }

            // 2. GÉRER les cadeaux orphelins (sauvetage ou suppression)
            for (const gift of existingCouponGifts) {
                const variantIdStr = String(gift.variant_id);

                if (!couponVariantIds.includes(variantIdStr)) {
                    // Si l'utilisateur a supprimé manuellement via le bouton de suppression
                    if (sessionStorage.getItem('kea_manual_remove_' + variantIdStr)) {
                        await removeGiftAction(gift.key);
                        sessionStorage.removeItem('kea_manual_remove_' + variantIdStr);
                        needsRefresh = true;
                        continue;
                    }

                    // Tentative de sauvetage (Max 2 tentatives pour éviter les boucles infinies)
                    const attempts = rescueAttempts[variantIdStr] || 0;
                    if (attempts < 2) {
                        rescueAttempts[variantIdStr] = attempts + 1;

                        const newCart = await applyDiscountCodeWithPersistence('KEA' + variantIdStr);

                        if (newCart && extractCouponVariantIds(newCart).includes(variantIdStr)) {
                            isUpdating = false;
                            refreshDrawer();
                            return;
                        }
                    } else {
                        await removeGiftAction(gift.key);
                        needsRefresh = true;
                    }
                } else {
                    rescueAttempts[variantIdStr] = 0;
                }
            }

            if (needsRefresh) refreshDrawer();
        } finally {
            isUpdating = false;
        }
    }

    /**
     * Action d'ajout — utilise nativeFetch pour éviter de déclencher les intercepteurs
     */
    async function addGiftAction(variantId) {
        try {
            await nativeFetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: [{ id: variantId, quantity: 1, properties: { '_source': 'coupon_gift', '_gift': 'true' } }]
                })
            });
        } catch (e) { }
    }

    /**
     * Action de retrait — utilise nativeFetch
     */
    async function removeGiftAction(key) {
        try {
            await nativeFetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: String(key), quantity: 0 })
            });
        } catch (e) { }
    }

    /**
     * Action pour forcer une quantité — utilise nativeFetch
     */
    async function setGiftQuantity(key, qty) {
        try {
            await nativeFetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: String(key), quantity: qty })
            });
        } catch (e) { }
    }

    /**
     * Planifie un check avec debounce pour éviter les cascades
     */
    function scheduleCheck(delay) {
        if (checkTimer) clearTimeout(checkTimer);
        checkTimer = setTimeout(() => {
            checkTimer = null;
            if (!isUpdating) {
                nativeFetch('/cart.js').then(res => res.json()).then(cart => checkCouponGifts(cart));
            }
        }, delay);
    }

    // --- Initialisation ---

    // Listener de soumission intelligent (Coopératif avec discount-drawer.js)
    document.addEventListener('submit', async (ev) => {
        const form = ev.target.closest('#drawer-discount-form');
        if (!form || form.dataset.keaProcessing) return;

        const input = form.querySelector('#drawer-discount-input');
        const code = (input?.value || '').trim();
        const match = code.match(/^KEA(?:-ID-)?(\d+)$/i);

        if (match && match[1]) {
            ev.preventDefault();
            ev.stopImmediatePropagation(); // Bloque temporairement discount-drawer.js

            form.dataset.keaProcessing = 'true';

            // Vérifier si le cadeau est déjà là avant de l'ajouter aveuglément
            const cart = await nativeFetch('/cart.js').then(res => res.json());
            const giftItems = cart.items.filter(item =>
                String(item.variant_id) === String(match[1]) &&
                item.properties &&
                item.properties._source === 'coupon_gift'
            );

            if (giftItems.length === 0) {
                await addGiftAction(match[1]);
            } else if (giftItems[0].quantity !== 1) {
                await setGiftQuantity(giftItems[0].key, 1);
            }

            // Relancer la soumission pour que discount-drawer.js applique le code promo
            const applyBtn = form.querySelector('.input-group__apply');
            if (applyBtn) {
                applyBtn.click();
            }

            setTimeout(() => { delete form.dataset.keaProcessing; }, 2000);
        }
    }, true); // Capture phase pour passer avant discount-drawer.js

    // Détection des suppressions manuelles
    document.addEventListener('click', (ev) => {
        const btn = ev.target.closest('[data-action="remove-discount"]');
        if (btn) {
            const code = String(btn.dataset.code || '').toUpperCase();
            const match = code.match(/^KEA(?:-ID-)?(\d+)$/i);
            if (match && match[1]) {
                sessionStorage.setItem('kea_manual_remove_' + match[1], 'true');
            }
        }
    });

    // Interception des appels Fetch (Monkey Patching) pour synchronisation en temps réel
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
        const response = await originalFetch.apply(this, args);
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;

        // Si c'est une action sur le panier, on planifie une vérification (avec debounce)
        if (url && (url.includes('/cart/add') || url.includes('/cart/update') || url.includes('/cart/change') || url.includes('/cart/clear'))) {
            if (!isUpdating) {
                scheduleCheck(500);
            }
        }
        return response;
    };

    // Listeners d'événements thématiques
    document.documentElement.addEventListener('cart:refresh', () => {
        if (!isUpdating) {
            scheduleCheck(800);
        }
    });

    // Premier check au chargement
    scheduleCheck(300);

})(document);
